"use client";

import type { UIMessage } from "@convex-dev/agent";
import { useCallback, useState } from "react";
import type { MessengerBubbleItem } from "./messenger-bubbles";

interface PendingSend {
  readonly text: string;
  /** Own rows loaded at send time; null = thread not loaded yet. */
  readonly knownKeys: ReadonlySet<string> | null;
  readonly sent: boolean;
}

export interface MessengerPendingSend {
  /** Own message as typed, until its stored row arrives. */
  readonly bubble: MessengerBubbleItem | null;
  /** Shows the bubble at once, runs `send`; a failed send removes it. */
  readonly track: (text: string, send: () => Promise<boolean>) => Promise<boolean>;
}

function ownKeys(rows: readonly UIMessage[]): Set<string> {
  return new Set(rows.filter((row) => row.role === "user").map((row) => row.key));
}

function isStored(pending: PendingSend, rows: readonly UIMessage[]): boolean {
  const own = rows.filter((row) => row.role === "user");
  if (pending.knownKeys !== null) {
    const known = pending.knownKeys;
    return own.some((row) => !known.has(row.key));
  }
  // Thread was still loading at send time: no baseline, so wait for the send itself.
  const last = own[own.length - 1];
  return last !== undefined && (pending.sent || last.text.trim() === pending.text);
}

/** Optimistic own message: one send at a time, as the composer allows. */
export function useMessengerPendingSend(
  rows: readonly UIMessage[],
  loaded: boolean,
): MessengerPendingSend {
  const [pending, setPending] = useState<PendingSend | null>(null);

  const track = useCallback(
    async (text: string, send: () => Promise<boolean>): Promise<boolean> => {
      const staged: PendingSend = { text, knownKeys: loaded ? ownKeys(rows) : null, sent: false };
      setPending(staged);
      const ok = await send();
      setPending((current) => (current !== staged ? current : ok ? { ...staged, sent: true } : null));
      return ok;
    },
    [loaded, rows],
  );

  const bubble: MessengerBubbleItem | null =
    pending === null || isStored(pending, rows)
      ? null
      : { id: "pending-send", author: "self", name: null, text: pending.text, at: null };
  return { bubble, track };
}
